import type { ResumeDocument } from '@docbee/resume';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type DragEventHandler, useCallback, useState } from 'react';
import { Logo } from '@/components/Logo';
import { db } from '@/lib/db';

export const Route = createFileRoute('/new')({
  component: RouteComponent,
});

interface ImportedData {
  resume?: ResumeDocument;
  generator?: string;
  generatorOptions?: Record<string, unknown>;
}

function isWrappedImport(data: unknown): data is ImportedData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'resume' in data &&
    typeof (data as ImportedData).resume === 'object'
  );
}

function RouteComponent() {
  const navigate = useNavigate();
  const [resumeID, setResumeID] = useState('');
  const [importedData, setImportedData] = useState<ImportedData | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      if (isWrappedImport(parsed)) {
        // Wrapped format: { resume: {...}, generator?: string, generatorOptions?: {...} }
        setImportedData(parsed);
      } else if (typeof parsed === 'object' && parsed !== null) {
        // Bare resume document — treat the whole object as the resume
        setImportedData({ resume: parsed as ResumeDocument, generator: 'block' });
      }
    } catch (error) {
      console.error('Failed to parse imported resume data:', error);
    }
  }, []);

  const handleDrop: DragEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && file.name.endsWith('.json')) {
        processFile(file);
      }
    },
    [processFile]
  );

  const handleDragOver: DragEventHandler<HTMLDivElement> = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave: DragEventHandler<HTMLDivElement> = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onSubmit = useCallback(
    async (e: React.SubmitEvent) => {
      e.preventDefault();
      if (!resumeID) return;

      if (importedData) {
        const content = JSON.stringify(importedData.resume, null, 2);
        const generator = importedData.generator || 'block';
        const generatorOptions = importedData.generatorOptions || {};

        await db.resumes.put({
          id: resumeID,
          generator,
          content,
          generatorOptions,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
      }

      navigate({
        to: '/edit/$resumeId',
        params: { resumeId: resumeID },
        search: { editor: undefined },
      });
    },
    [resumeID, importedData, navigate]
  );

  return (
    <div className="flex flex-col p-4 gap-3 mx-auto max-w-4xl w-full">
      <div className="flex flex-row items-center justify-between">
        <Link to="/">
          <Logo className="text-4xl" hoverZoom={true} />
        </Link>
      </div>

      <div className="flex flex-col gap-4">
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            value={resumeID}
            onChange={(e) => setResumeID(e.target.value)}
            placeholder="Enter resume ID"
            className="border-b-2 border-dotted p-1 rounded-sm border-neutral-400 font-mono"
            autoFocus
          />

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={
              'relative flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 transition-colors duration-200 ' +
              (isDragging
                ? 'border-blue-400 bg-blue-50'
                : 'border-neutral-400 hover:border-neutral-500')
            }
          >
            <p className="text-sm text-neutral-600 font-mono text-center">
              {isDragging
                ? 'Drop your .json file here'
                : importedData
                  ? `✓ Resume data imported (${importedData.resume?.name}) — drop a new file to replace`
                  : 'Drag & drop a .json file here to import resume data.'}
            </p>
            {importedData && (
              <p className="text-xs text-neutral-500 font-mono mt-2">
                Generator: {importedData.generator || 'block'}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="hidden border p-1 rounded-sm border-neutral-400 font-mono"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
}
